import React, { useEffect, useState, useCallback } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from "./../../ui/Button";
import UserService from "./../../../services/UserService";
import userService from "./../../../services/UserService";
import SearchableUserSelect from "../../SearchableUserSelect";

export default function DeleteUserModal({ isOpen, onClose, onSubmit }) {
  const [username, setUsername] = useState("");
  const [userPlatform, setUserPlatform] = useState("mobile");
  const [confirmText, setConfirmText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState({});

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const MIN_QUERY = 3;

  useEffect(() => {
    if (!isOpen) return;
    setUsername("");
    setConfirmText("");
    setUsers([]);
    setErr({});
    setUserPlatform("mobile");
  }, [isOpen]);

  const fetchUsers = useCallback(async (query = "") => {
    try {
      setUsersLoading(true);

      if (!query || query.length < MIN_QUERY) return;

      const response =
        userPlatform === "mobile"
          ? await userService.getAnalystsList(query)
          : await userService.getInstallAccountNames(query);

      const raw = Array.isArray(response) ? response : [];

      const normalized = raw.map((u, idx) => ({
        id: u.id ?? u.userId ?? u.visorId ?? idx,
        username:
          u.username ??
          u.userName ??
          u.name ??
          u.displayName ??
          u.email ??
          u.visorId ??
          "Unknown",
      }));

      setUsers(normalized);
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [userPlatform]);

  const handleUserQueryChange = useCallback(
    (q) => {
      if (q && q.length >= 3) {
        fetchUsers(q);
      }
    },
    [fetchUsers]
  );

  const handleSelectUser = useCallback(
    async (selectedUsername) => {
      const u = (selectedUsername || "").trim();
      if (!u) return;

      setSaving(true);
      try {
        const user =
          userPlatform === "mobile"
            ? await UserService.getMobileUser(u)
            : await UserService.getPoliceUser(u);

        if (!user) {
          setErr({ username: "User not found." });
          return;
        }

        setUsername(user.username ?? u);
      } catch (error) {
        setErr({ username: error.message });
      } finally {
        setSaving(false);
      }
    },
    [userPlatform]
  );

  const submit = async () => {
    if (!username.trim()) {
      setErr({ username: "Please select a user." });
      return;
    }

    if (confirmText.trim().toLowerCase() !== "delete") {
      setErr({ confirm: "Type 'delete' to confirm." });
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        username: username.trim(),
        platform: userPlatform,
      });
      onClose();
    } catch (ex) {
      setErr({ api: ex.message || "Failed to delete user." });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop">
      <div className="sd-modal sd-modal-md">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Delete user</h2>
            <div className="sd-subtitle">
              Search and permanently remove a user account.
            </div>
          </div>
          <button className="sd-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="sd-modal-body">

          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}

          <div className="sd-alert sd-alert-bad">
            ⚠️ This action is permanent and cannot be undone.
          </div>

          <div className="sd-field">
            <div className="sd-label">User Platform</div>
            <div className="sd-seg">
              <button
                className={`sd-seg-btn ${userPlatform === "mobile" ? "is-active" : ""}`}
                onClick={() => {
                  setUsername("");
                  setUsers([]);
                  setUserPlatform("mobile");
                }}
              >
                Mobile
              </button>
              <button
                className={`sd-seg-btn ${userPlatform === "pc" ? "is-active" : ""}`}
                onClick={() => {
                  setUsername("");
                  setUsers([]);
                  setUserPlatform("pc");
                }}
              >
                Install Portal Account
              </button>
            </div>
          </div>

          <div className="sd-field">
            <div className="sd-label">User</div>
            <SearchableUserSelect
              value={username}
              onSelect={handleSelectUser}
              options={users}
              loading={usersLoading}
              onQueryChange={handleUserQueryChange}
            />
            {err.username && <div className="sd-error">{err.username}</div>}
          </div>

          <div className="sd-divider" />
          <div className="sd-field">
            <div className="sd-label">Type delete to confirm</div>
            <input
              className={`sd-input ${err.confirm ? "has-error" : ""}`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            {err.confirm && <div className="sd-error">{err.confirm}</div>}
          </div>
        </div>

        {/* FOOTER */}
        <div className="sd-modal-footer">
          <Button onClick={onClose} buttonStyle="btn-alternate">
            Cancel
          </Button>
          <Button onClick={submit} buttonStyle="btn-danger">
            {saving ? "Deleting…" : "Delete user"}
          </Button>
        </div>
      </div>
    </div>
  );
}